import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChefHat, ShieldCheck, MapPin, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function EntryScreen() {
    const navigate = useNavigate();

    const [logoClicks, setLogoClicks] = useState(0);
    const [adminUnlocked, setAdminUnlocked] = useState(false);

    const handleLogoClick = () => {
        const nextClicks = logoClicks + 1;
        setLogoClicks(nextClicks);

        if (nextClicks >= 5) {
            setAdminUnlocked(true);
        }
    };

    return (
        <div
            className="min-h-screen flex flex-col bg-pz-bg paper-grain"
            data-testid="entry-screen"
        >
            <header className="px-6 sm:px-10 pt-8 sm:pt-10 flex items-center justify-between">
                <button
                    type="button"
                    onClick={handleLogoClick}
                    data-testid="entry-logo-secret-btn"
                    className="flex items-center gap-3 text-left"
                    aria-label="Pizano logo"
                >
                    <div className="w-10 h-10 rounded-full bg-pz-primary text-white grid place-items-center">
                        <ChefHat size={20} />
                    </div>
                    <span className="font-display text-2xl tracking-wide text-pz-text">
                        PIZANO
                    </span>
                </button>

                <Link
                    to="/track"
                    data-testid="entry-track-link"
                    className="text-sm font-semibold text-pz-text hover:text-pz-primary transition-colors uppercase tracking-wider"
                >
                    Track Order
                </Link>
            </header>

            <main className="flex-1 grid place-items-center px-6 py-10">
                <div className="max-w-3xl w-full text-center animate-float-up">
                    <p className="text-xs sm:text-sm uppercase tracking-[0.4em] text-pz-primary font-semibold mb-4">
                        Authentic Italian — Malé, Maldives
                    </p>

                    <h1 className="font-display text-6xl sm:text-7xl md:text-8xl text-pz-text leading-none mb-3">
                        Pizano{" "}
                        <span className="text-pz-primary">
                            Italiano
                        </span>
                    </h1>

                    <p className="text-base sm:text-lg text-pz-textMuted max-w-xl mx-auto mb-12">
                        Loaded dirty fries, wood-fired pizzas & cozy comfort
                        food. Order for delivery or pickup — straight from our
                        kitchen to your table.
                    </p>

                    <div
                        className={`grid grid-cols-1 ${
                            adminUnlocked ? "sm:grid-cols-2" : "sm:grid-cols-1"
                        } gap-5 max-w-xl mx-auto`}
                    >
                        <button
                            onClick={() => navigate("/menu")}
                            data-testid="entry-customer-btn"
                            className="group relative overflow-hidden rounded-2xl border border-pz-border bg-white p-7 text-left transition-all hover:-translate-y-1 hover:shadow-xl hover:border-pz-primary"
                        >
                            <div className="w-12 h-12 rounded-full bg-pz-primary/10 text-pz-primary grid place-items-center mb-4 group-hover:bg-pz-primary group-hover:text-white transition-colors">
                                <ChefHat size={22} />
                            </div>

                            <div className="font-display text-3xl text-pz-text mb-1">
                                Customer
                            </div>

                            <p className="text-sm text-pz-textMuted">
                                Browse menu, order food & track your delivery
                            </p>
                        </button>

                        {adminUnlocked && (
                            <button
                                onClick={() => navigate("/admin")}
                                data-testid="entry-admin-btn"
                                className="group relative overflow-hidden rounded-2xl border border-pz-border bg-pz-text p-7 text-left text-white transition-all hover:-translate-y-1 hover:shadow-xl"
                            >
                                <div className="w-12 h-12 rounded-full bg-white/10 grid place-items-center mb-4 group-hover:bg-pz-primary transition-colors">
                                    <ShieldCheck size={22} />
                                </div>

                                <div className="font-display text-3xl mb-1">
                                    Admin
                                </div>

                                <p className="text-sm text-white/70">
                                    Manage incoming orders & update statuses
                                </p>
                            </button>
                        )}
                    </div>

                    <div className="mt-14 grid grid-cols-2 gap-4 max-w-xl mx-auto text-pz-textMuted text-xs sm:text-sm">
                        <div className="flex items-center justify-center gap-2">
                            <MapPin
                                size={14}
                                className="text-pz-primary shrink-0"
                            />{" "}
                            Malé, Maldives
                        </div>

                        <div className="flex items-center justify-center gap-2">
                            <Phone
                                size={14}
                                className="text-pz-primary shrink-0"
                            />{" "}
                            +960 only
                        </div>
                    </div>
                </div>
            </main>

            <footer className="px-6 py-6 text-center text-xs text-pz-textMuted">
                © {new Date().getFullYear()} Pizano Italiano. Made with love in
                Malé.
            </footer>
        </div>
    );
}
